import { ChangeEvent, KeyboardEvent, useState } from 'react'
import { faCircle, faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { PropertyType } from '../../_types/property'
import { DiscoverFilters } from '../../_types/quickpass-discover'
import { QuickPassDwellingScope } from '../../_types/quickpass-profile'
import {
  BATHROOM_FILTER_OPTIONS,
  BEDROOM_FILTER_OPTIONS,
  DWELLING_SCOPE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  countActiveFilters,
} from '../../pages/discover/helpers'
import {
  ActiveCountChip,
  BarRoot,
  ChipButton,
  ChipRow,
  CityInput,
  CityTag,
  CityTagRemoveButton,
  CityTagRow,
  ExpandedPanel,
  PanelLabel,
  PanelSection,
  PriceInput,
  PriceInputRow,
  PriceSeparator,
  ResetButton,
  SearchIcon,
  SearchInput,
  SearchWrapper,
  TopRow,
} from './styled'
import { DiscoverFilterBarProps } from './types'

const toggleInArray = <T,>(array: T[], value: T): T[] => {
  if (array.includes(value)) {
    return array.filter(existing => existing !== value)
  }
  return [...array, value]
}

const parseOptionalPrice = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (trimmed === '') {
    return null
  }
  const parsed = Number.parseInt(trimmed.replace(/[^0-9]/gu, ''), 10)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Top-of-page filter bar for /discover. Search input + chip toggles for
 * property type live on the top row; an expanded panel under it carries
 * price, beds, baths, cities, dwelling scope. Filter changes bubble up
 * through `onFiltersChange` — debouncing is the parent's call.
 *
 * @param {DiscoverFilterBarProps} props
 * @returns {JSX.Element}
 */
const DiscoverFilterBar = ({
  filters,
  isLoading,
  onFiltersChange,
  onResetFilters,
}: DiscoverFilterBarProps): JSX.Element => {
  const [cityDraft, setCityDraft] = useState<string>('')

  const activeCount = countActiveFilters(filters)

  const update = (patch: Partial<DiscoverFilters>): void => {
    onFiltersChange({ ...filters, ...patch })
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    update({ searchText: event.target.value })
  }

  const handleTogglePropertyType = (value: PropertyType): void => {
    update({ propertyTypes: toggleInArray(filters.propertyTypes, value) })
  }

  const handleToggleBedrooms = (value: number): void => {
    update({ bedroomCounts: toggleInArray(filters.bedroomCounts, value) })
  }

  const handleToggleBathrooms = (value: number): void => {
    update({ bathroomCounts: toggleInArray(filters.bathroomCounts, value) })
  }

  const handlePickDwellingScope = (value: QuickPassDwellingScope): void => {
    update({ dwellingScope: value })
  }

  const handleToggleAvailability = (): void => {
    update({
      availabilityFilter:
        filters.availabilityFilter === 'available-now'
          ? 'all'
          : 'available-now',
    })
  }

  const handlePriceMinChange = (event: ChangeEvent<HTMLInputElement>): void => {
    update({ priceMin: parseOptionalPrice(event.target.value) })
  }

  const handlePriceMaxChange = (event: ChangeEvent<HTMLInputElement>): void => {
    update({ priceMax: parseOptionalPrice(event.target.value) })
  }

  const handleAddCity = (): void => {
    const trimmed = cityDraft.trim()
    if (trimmed === '') {
      return
    }
    if (
      filters.cities.some(city => city.toLowerCase() === trimmed.toLowerCase())
    ) {
      setCityDraft('')
      return
    }
    update({ cities: [...filters.cities, trimmed] })
    setCityDraft('')
  }

  const handleRemoveCity = (city: string): void => {
    update({ cities: filters.cities.filter(existing => existing !== city) })
  }

  const handleCityKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAddCity()
    }
  }

  return (
    <BarRoot>
      <TopRow>
        <SearchWrapper>
          <SearchIcon aria-hidden="true">
            <Icon icon={faMagnifyingGlass} label="Search" />
          </SearchIcon>
          <SearchInput
            type="search"
            value={filters.searchText}
            onChange={handleSearchChange}
            placeholder="Search by neighbourhood, building, vibe…"
            aria-label="Search discover listings"
          />
        </SearchWrapper>
        {PROPERTY_TYPE_OPTIONS.map(option => {
          const isActive = filters.propertyTypes.includes(option.value)
          return (
            <ChipButton
              key={option.value}
              type="button"
              isActive={isActive}
              onClick={() => handleTogglePropertyType(option.value)}
              aria-pressed={isActive}
            >
              <Icon icon={option.icon} />
              {option.label}
            </ChipButton>
          )
        })}
        <ChipButton
          type="button"
          isActive={filters.availabilityFilter === 'available-now'}
          onClick={handleToggleAvailability}
          aria-pressed={filters.availabilityFilter === 'available-now'}
        >
          <Icon icon={faCircle} color="#22c55e" /> Available now
        </ChipButton>
        {activeCount > 0 ? (
          <ResetButton type="button" onClick={onResetFilters}>
            Reset
            <ActiveCountChip>{activeCount}</ActiveCountChip>
          </ResetButton>
        ) : null}
      </TopRow>

      <ExpandedPanel>
        <PanelSection>
          <PanelLabel>Price / month</PanelLabel>
          <PriceInputRow>
            <PriceInput
              type="number"
              value={filters.priceMin ?? ''}
              onChange={handlePriceMinChange}
              placeholder="Min"
              min={0}
              step={50}
              aria-label="Minimum price"
            />
            <PriceSeparator>to</PriceSeparator>
            <PriceInput
              type="number"
              value={filters.priceMax ?? ''}
              onChange={handlePriceMaxChange}
              placeholder="Max"
              min={0}
              step={50}
              aria-label="Maximum price"
            />
          </PriceInputRow>
        </PanelSection>

        <PanelSection>
          <PanelLabel>Bedrooms</PanelLabel>
          <ChipRow>
            {BEDROOM_FILTER_OPTIONS.map(option => {
              const isActive = filters.bedroomCounts.includes(option.value)
              return (
                <ChipButton
                  key={option.value}
                  type="button"
                  isActive={isActive}
                  onClick={() => handleToggleBedrooms(option.value)}
                  aria-pressed={isActive}
                >
                  {option.label}
                </ChipButton>
              )
            })}
          </ChipRow>
        </PanelSection>

        <PanelSection>
          <PanelLabel>Bathrooms</PanelLabel>
          <ChipRow>
            {BATHROOM_FILTER_OPTIONS.map(option => {
              const isActive = filters.bathroomCounts.includes(option.value)
              return (
                <ChipButton
                  key={option.value}
                  type="button"
                  isActive={isActive}
                  onClick={() => handleToggleBathrooms(option.value)}
                  aria-pressed={isActive}
                >
                  {option.label}
                </ChipButton>
              )
            })}
          </ChipRow>
        </PanelSection>

        <PanelSection>
          <PanelLabel>Whole place or unit</PanelLabel>
          <ChipRow>
            {DWELLING_SCOPE_OPTIONS.map(option => {
              const isActive = filters.dwellingScope === option.value
              return (
                <ChipButton
                  key={option.value}
                  type="button"
                  isActive={isActive}
                  onClick={() => handlePickDwellingScope(option.value)}
                  aria-pressed={isActive}
                >
                  {option.icon !== undefined ? (
                    <Icon icon={option.icon} />
                  ) : null}
                  {option.title}
                </ChipButton>
              )
            })}
          </ChipRow>
        </PanelSection>

        <PanelSection>
          <PanelLabel>Cities</PanelLabel>
          <CityTagRow>
            {filters.cities.map(city => (
              <CityTag key={city}>
                {city}
                <CityTagRemoveButton
                  type="button"
                  onClick={() => handleRemoveCity(city)}
                  aria-label={`Remove ${city}`}
                  title={`Remove ${city}`}
                >
                  ×
                </CityTagRemoveButton>
              </CityTag>
            ))}
            <CityInput
              type="text"
              value={cityDraft}
              onChange={event => setCityDraft(event.target.value)}
              onKeyDown={handleCityKeyDown}
              onBlur={handleAddCity}
              placeholder={
                filters.cities.length === 0
                  ? 'e.g. Toronto, then Enter'
                  : 'Add another…'
              }
              aria-label="Add city"
              disabled={isLoading}
            />
          </CityTagRow>
        </PanelSection>
      </ExpandedPanel>
    </BarRoot>
  )
}

export default DiscoverFilterBar
